import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import type { CoGuardian, ProfileAction } from '../types';
import { getGuardianProfileVersionLabel } from '../utils';

import { useOpenShop } from '@/features/shop/hooks/use-open-shop';
import { DebugLogSheet } from './debug-log-sheet';
import { ProfileActionSection } from './profile-action-section';
import { ProfileIdentity } from './profile-identity';
import { ReportIssueSheet } from './report-issue-sheet';

type Props = {
  avatarUri?: string;
  name: string;
  onLogout: () => void;
  tablet?: boolean;
  selectedSettingKey?: string;
  onSelectSetting?: (action: ProfileAction) => void;
  coGuardians: CoGuardian[];
};

const getOrderActions = (tablet: boolean, openMyOrders: () => void): ProfileAction[] => [
  {
    key: 'orders',
    label: tablet ? STRINGS.guardianProfile.myOrder : STRINGS.guardianProfile.myOrders,
    icon: 'IcUserSquare',
    onPress: openMyOrders,
  },
];
const PHONE_SETTINGS_ACTIONS: ProfileAction[] = [
  {
    key: 'security',
    label: STRINGS.guardianProfile.settings.security,
    icon: 'IcUserOctagon',
  },
  {
    key: 'notifications',
    label: STRINGS.guardianProfile.settings.notifications,
    icon: 'IcProfileNotification',
  },
  { key: 'support', label: STRINGS.guardianProfile.settings.support, icon: 'IcMessageQuestion' },
  { key: 'legal', label: STRINGS.guardianProfile.settings.legal, icon: 'IcPrivacyLegal' },
];

/** Settings row that opens the "Report an issue" sheet instead of navigating to a screen. */
export const REPORT_ISSUE_SETTING_KEY = 'reportIssue';

export function ProfilePanel({
  avatarUri,
  name,
  onLogout,
  tablet = false,
  selectedSettingKey,
  onSelectSetting,
}: Props) {
  const { scale } = useScaling();
  const styles = useStyles();
  const openShop = useOpenShop();

  // Hidden tester tool: tapping the version label 10 times in quick succession
  // opens the debug log panel. No visible affordance, so it's safe in production.
  const [debugOpen, setDebugOpen] = useState(false);
  const tapCountRef = useRef(0);
  const lastTapRef = useRef(0);

  const handleVersionTap = useCallback(() => {
    const now = Date.now();
    // A gap longer than the window restarts the count so ordinary taps never add up.
    tapCountRef.current = now - lastTapRef.current > 1500 ? 1 : tapCountRef.current + 1;
    lastTapRef.current = now;
    if (tapCountRef.current >= 10) {
      tapCountRef.current = 0;
      setDebugOpen(true);
    }
  }, []);
  // "Report an issue" opens a bottom sheet here rather than routing anywhere, so it owns its
  // own open state and carries an `onPress` (the screen skips it when picking a tablet panel).
  const [reportOpen, setReportOpen] = useState(false);

  const settingsActions = useMemo<ProfileAction[]>(() => {
    const reportAction: ProfileAction = {
      key: REPORT_ISSUE_SETTING_KEY,
      label: STRINGS.guardianProfile.settings.reportIssue,
      icon: 'IcReport',
      onPress: () => setReportOpen(true),
    };

    return tablet
      ? [
          {
            key: 'account',
            label: STRINGS.guardianProfile.settings.account,
            icon: 'IcUserSquare' as const,
          },
          ...PHONE_SETTINGS_ACTIONS,
          reportAction,
        ]
      : [...PHONE_SETTINGS_ACTIONS, reportAction];
  }, [tablet]);

  const openMyOrders = () =>
    openShop(
      'https://shopify.com/99670950163/account/logout?return_url=https%3A%2F%2Fuskg-sandbox.myshopify.com%2Fcustomer_authentication%2Flogin%3Freturn_to%3D%252Fa%252Fcustomerhub%2523account%253Aa%253Aorders',
    );

  return (
    <Stack style={[styles.root, tablet ? styles.tabletRoot : null]} gap={scale(20)}>
      <ProfileIdentity name={name} avatarUri={avatarUri} tablet={tablet} />
      <Stack style={tablet ? styles.tabletInfoCard : undefined} gap={scale(20)}>
        {/* {coGuardians.length > 0 ? (
          <Stack style={tablet ? styles.tabletSection : undefined}>
            <CoGuardiansSection guardians={coGuardians} tablet={tablet} />
          </Stack>
        ) : null} */}
        <Stack style={tablet ? styles.tabletSection : undefined}>
          <ProfileActionSection
            title={STRINGS.guardianProfile.productOrders}
            actions={getOrderActions(tablet, openMyOrders)}
            tablet={tablet}
          />
        </Stack>
      </Stack>
      <Stack style={tablet ? styles.tabletSettingsSection : undefined}>
        <ProfileActionSection
          title={STRINGS.guardianProfile.settings.title}
          actions={settingsActions}
          selectedKey={tablet ? selectedSettingKey : undefined}
          onSelect={onSelectSetting}
          tablet={tablet}
        />
      </Stack>
      <DebugLogSheet open={debugOpen} onOpenChange={setDebugOpen} />
      <ReportIssueSheet open={reportOpen} onOpenChange={setReportOpen} />
      <Pressable onPress={handleVersionTap}>
        <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.version}>
          {getGuardianProfileVersionLabel(tablet)}
        </Text>
      </Pressable>
      {!tablet ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={STRINGS.guardianProfile.logout.accessibilityLabel}
          onPress={onLogout}
          style={({ pressed }) => [styles.logoutButton, pressed ? styles.pressed : null]}>
          <SvgIcon name="IcProfileLogout" size={scale(20)} />
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold" style={styles.logoutText}>
            {STRINGS.guardianProfile.logout.phone}
          </Text>
        </Pressable>
      ) : null}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: { width: '100%' },
  tabletRoot: {
    width: scale(338),
    backgroundColor: 'transparent',
  },
  tabletInfoCard: {
    paddingVertical: scale(20),
    borderRadius: scale(12),
    backgroundColor: colors['0'],
  },
  tabletSection: { paddingHorizontal: scale(20) },
  tabletSettingsSection: {
    padding: scale(20),
    borderRadius: scale(12),
    backgroundColor: colors['0'],
  },
  version: { color: guardianColors['6'], textAlign: 'center' },
  logoutButton: {
    height: scale(48),
    borderRadius: scale(12),
    borderCurve: 'continuous',
    borderWidth: 0.5,
    borderColor: guardianColors.Error,
    backgroundColor: colors['0'],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: scale(8),
    shadowColor: colors['10'],
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 1,
    elevation: 1,
  },
  logoutText: { color: guardianColors.Error },
  pressed: { opacity: 0.68 },
}));
